import { React, useState, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import {
  ConfigurationService,
  BlockchainService,
  Spinner,
  Alert,
} from '../../services';
import { ProcessingIcon } from '../../components/icons/IconProvider';
import FormInput from '../../components/FormInput';
// const SEPA = require('sepa');
const Configuration = () => {
  const [isLoader, setLoader] = useState(false);
  const schema = Yup.object({
    assetHolderMinBalance: Yup.number()
      .typeError('Asset manager min balance is required.')
      .required('Asset manager min balance is required.')
      .nullable(),
    accountNumberIban: Yup.string()
      .required('Admin Account Number (IBAN) is required')
      .test('IBAN', 'Account number (IBAN) invalid', async (val) => {
        const mod = await import('sepa').catch(() => null);
        const SEPA = mod ? mod.default || mod : null;
        return SEPA ? SEPA.validateIBAN(val) : false;
      })
      .nullable(),
    accountHolderName: Yup.string()
      .required('Account holder name is required')
      .nullable(),
    percentageOnTotalBlocks: Yup.number()
      .typeError('Percentage value is required.')
      .required('Percentage value is required.')
      .max(100, 'Percentage value should not be greater than 100')
      .min(1, 'Percentage value should not be less than 1')
      .integer("A percentage value can't include a decimal point")
      .nullable(),
    sumsubTokenExpirySeconds: Yup.number()
      .typeError('Sumsub token expiry time must be a number.')
      .required('Sumsub token expiry time is required.')
      .min(60, 'Expiry time should be at least 60 seconds.')
      .nullable(),
  }).required();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const getConfigurationDetails = async () => {
    Spinner.show();
    try {
      let response = await ConfigurationService.getConfigurationDetails();

      if (response.status === 'failure') {
        Spinner.hide();
        Alert.error(response.message);
        return false;
      }
      Spinner.hide();
      let balance = await BlockchainService.formatEtherDecimals(
        response.data['assetHolder.thresholdBalanceWei'],
      );
      const savedValues = {
        assetHolderAddress: response.data['assetHolder.address']
          ? response.data['assetHolder.address']
          : '',
        notifySignupTo: response.data['admin.notifySignupTo']
          ? response.data['admin.notifySignupTo']
          : '',
        blockchainRpcUrl: response.data['chain.rpcUrl']
          ? response.data['chain.rpcUrl']
          : '',
        assetHolderMinBalance: balance,
        foundationWalletAddress: response.data['foundation.walletAddress']
          ? response.data['foundation.walletAddress']
          : '',
        accountNumberIban: response.data['foundation.IBAN']
          ? response.data['foundation.IBAN']
          : '',
        accountHolderName: response.data['foundation.accountHolderName']
          ? response.data['foundation.accountHolderName']
          : '',
        percentageOnTotalBlocks: response.data[
          'platform.maxPurchaseLimitInPercent'
        ]
          ? response.data['platform.maxPurchaseLimitInPercent']
          : '',
        sumsubTokenExpirySeconds: response.data[
          'platform.sumsubTokenExpirySeconds'
        ]
          ? response.data['platform.sumsubTokenExpirySeconds']
          : '',
      };

      reset(savedValues);
    } catch (error) {
      Spinner.hide();
      Alert.error(error.message);
      return false;
    }
  };
  useEffect(() => {
    getConfigurationDetails();
  }, []);
  const updateConfiguration = async (_formInput) => {
    try {
      setLoader(true);
      let balance = await BlockchainService.parseEtherDecimals(
        _formInput.assetHolderMinBalance.toString(),
      );
      const _generateDividend = {
        'assetHolder.thresholdBalanceWei': Number(balance),
        'foundation.IBAN': _formInput.accountNumberIban,
        'foundation.accountHolderName': _formInput.accountHolderName,
        'admin.notifySignupTo': _formInput.notifySignupTo,
        'platform.maxPurchaseLimitInPercent':
          _formInput.percentageOnTotalBlocks,
        'platform.sumsubTokenExpirySeconds':
          _formInput.sumsubTokenExpirySeconds,
      };

      let response = await ConfigurationService.update(_generateDividend);

      if (response.status === 'failure') {
        setLoader(false);
        Alert.error(response.message);
        return false;
      }
      Alert.success(response.message);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      Alert.error(error.message);
      return false;
    }
  };

  return (
    <div className="">
      <div className="grid grid-cols-2 mb-4 items-center">
        <h4 className="md:mb-2">Configuration</h4>
      </div>
      <div className="w-full bg-brandGray md:px-5 md:py-6 p-4 rounded-xl">
        <form onSubmit={handleSubmit(updateConfiguration)} className="w-full">
          <div className="w-full">
            <div className="grid md:grid-cols-2 gap-x-5 relative">
              <div className="md:col-span-2 mb-5">
                <h4>Wallet Configuration</h4>
              </div>
              <div className="mb-4">
                <FormInput
                  label=" Asset Manager Wallet Address"
                  name="assetHolderAddress"
                  type="text"
                  placeholder="Asset Holder Address"
                  register={register}
                  errors={errors}
                  readOnly={true}
                  className="bg-gray-200"
                  labelClassName="font-semibold"
                />
              </div>

              <div className="mb-4">
                <FormInput
                  label="Blockchain RPC URL"
                  name="blockchainRpcUrl"
                  type="text"
                  placeholder="Blockchain RPC URL"
                  register={register}
                  errors={errors}
                  readOnly={true}
                  className="bg-gray-200"
                  labelClassName="font-semibold"
                />
              </div>

              <div className="mb-4">
                <FormInput
                  label="Foundation Wallet Address"
                  name="foundationWalletAddress"
                  type="text"
                  placeholder="Foundation Wallet Address"
                  register={register}
                  errors={errors}
                  readOnly={true}
                  className="bg-gray-200"
                  labelClassName="font-semibold"
                />
              </div>

              <div className="mb-4">
                <FormInput
                  label="Asset Manager Min Balance"
                  name="assetHolderMinBalance"
                  type="text"
                  placeholder="Asset Manager Min Balance"
                  register={register}
                  errors={errors}
                  labelClassName="font-semibold"
                />
              </div>

              <div className="mb-4">
                <FormInput
                  label="Percentage of equity that may be owned by one person"
                  name="percentageOnTotalBlocks"
                  type="text"
                  placeholder="Percentage of equity"
                  register={register}
                  errors={errors}
                  labelClassName="font-semibold"
                />
              </div>

              <div className="mb-4">
                <FormInput
                  label="Notify Signup To"
                  name="notifySignupTo"
                  type="text"
                  placeholder="Notify Signup To"
                  register={register}
                  errors={errors}
                  labelClassName="font-semibold"
                />
              </div>

              <div className="mb-4">
                <FormInput
                  label={
                    <>
                      KYC Token Expiry Time{' '}
                      <span className="font-normal">(In seconds)</span>
                    </>
                  }
                  name="sumsubTokenExpirySeconds"
                  type="text"
                  placeholder="KYC Token Expiry Time"
                  register={register}
                  errors={errors}
                  labelClassName="font-semibold"
                />
              </div>

              <div className="md:col-span-2 my-5">
                <h4>Bank Configuration</h4>
              </div>

              <div className="mb-4">
                <FormInput
                  label={
                    <>
                      Account Number (IBAN)
                      <span className="text-xs text-gray-600 font-normal">
                        (E.g.: NL20INGB0001234567)
                      </span>
                    </>
                  }
                  name="accountNumberIban"
                  type="text"
                  placeholder="Ex. NL20INGB0001234567"
                  register={register}
                  errors={errors}
                  labelClassName="font-semibold"
                />
              </div>
              <div className="mb-4">
                <FormInput
                  label={
                    <>
                      Account Holder Name
                      <span className="text-xs text-gray-600 font-normal">
                        (E.g.: INGBNL2A)
                      </span>
                    </>
                  }
                  name="accountHolderName"
                  type="text"
                  placeholder="Ex. INGBNL2A"
                  register={register}
                  errors={errors}
                  labelClassName="font-semibold"
                />
              </div>
              <div className="md:col-span-2 text-center mt-5">
                <button
                  type="submit"
                  className={
                    isLoader
                      ? 'bg-[#787a7d] text-sm cursor-not-allowed text-brandBlue font-semibold py-3'
                      : 'text-sm bg-GreenColor text-brandBlue font-semibold py-3'
                  }
                  disabled={isLoader}
                >
                  {isLoader ? (
                    <div className="flex items-center gap-2">
                      <ProcessingIcon />
                      Updating
                    </div>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
export default Configuration;
